import { FILTER_TYPES } from '@/constants/filter';
import { NumericRangeFilterParam } from '@/enums/FilterParam';
import {
	EnumsFilterInput,
	NumberRangeInput,
	NumericRangeFilterInput,
} from '@/features/FeatureRefinedProductList/actions';
import { NumericRangeProductFilterConfiguration, ProductFilter } from '@/types/gearApi/Product';

type RangeTuple = [number?, number?];
type FilterRangeLimits = Record<string, { min: number; max: number }>;

function isRangeKey(key: string): boolean {
	return key.endsWith(NumericRangeFilterParam.From) || key.endsWith(NumericRangeFilterParam.To);
}

function extractBaseKey(key: string): string {
	return key.replace(new RegExp(`(${NumericRangeFilterParam.From}|${NumericRangeFilterParam.To})$`), '');
}

function parseNumber(value: string | number | undefined): number | undefined {
	if (typeof value === 'number') return isNaN(value) ? undefined : value;
	if (typeof value === 'string') {
		const num = Number(value);
		return isNaN(num) ? undefined : num;
	}
	return undefined;
}

function processEntry(key: string, value: string | number | undefined, temp: Record<string, RangeTuple>): void {
	if (!isRangeKey(key)) return;

	const baseKey = extractBaseKey(key);
	const num = parseNumber(value);
	if (num === undefined) return;

	if (!temp[baseKey]) temp[baseKey] = [undefined, undefined];
	if (key.endsWith(NumericRangeFilterParam.From)) temp[baseKey][0] = num;
	if (key.endsWith(NumericRangeFilterParam.To)) temp[baseKey][1] = num;
}

/**
 * Converts query-like input (URLSearchParams or plain object) into normalized
 * numeric range tuples keyed by filter attribute.
 *
 * If a `limits` object is provided, the parsed `from` and `to` values will be clamped within
 * the configured min/max bounds for each attribute.
 *
 * Only ranges where `from < to` are included in the result.
 *
 * @example
 * Input: ?maximumFlowInLpmFrom=10&maximumFlowInLpmTo=100
 * Output: { maximumFlowInLpm: [10, 100] }
 *
 * Input with clamping: ?flowRateFrom=-1&flowRateTo=999999
 * Limits: { flowRate: { min: 0, max: 1000 } }
 * Output: { flowRate: [0, 1000] }
 */
export function parseNumericRangeParams(
	input: URLSearchParams | NumberRangeInput,
	limits?: FilterRangeLimits,
): Record<string, [number, number]> {
	const temp: Record<string, RangeTuple> = {};
	const entries = input instanceof URLSearchParams ? Array.from(input.entries()) : Object.entries(input);

	for (const [key, value] of entries) {
		processEntry(key, value, temp);
	}

	const result: Record<string, [number, number]> = {};

	for (const [key, [from, to]] of Object.entries(temp)) {
		if (from !== undefined && to !== undefined) {
			let clampedFrom = from;
			let clampedTo = to;

			if (limits?.[key]) {
				const { min, max } = limits[key];
				clampedFrom = Math.max(from, min);
				clampedTo = Math.min(to, max);
			}

			if (clampedFrom < clampedTo) {
				result[key] = [clampedFrom, clampedTo];
			}
		}
	}

	return result;
}

export function buildDynamicRangeParams(numericRanges: NumericRangeFilterInput = {}): NumberRangeInput {
	const dynamicRangeParams: NumberRangeInput = {};
	for (const [key, range] of Object.entries(numericRanges)) {
		if (range && range[0] !== undefined && range[1] !== undefined) {
			dynamicRangeParams[`${key}${NumericRangeFilterParam.From}`] = range[0];
			dynamicRangeParams[`${key}${NumericRangeFilterParam.To}`] = range[1];
		}
	}
	return dynamicRangeParams;
}

export function extractFilterLimits(config: ProductFilter[]): FilterRangeLimits {
	const limits: FilterRangeLimits = {};

	config.forEach((filter) => {
		if (filter.configuration.$type === FILTER_TYPES.numericRangeFilter) {
			const { minValue, maxValue, attribute } = filter.configuration as NumericRangeProductFilterConfiguration;
			if (minValue != null && maxValue != null) {
				limits[attribute] = { min: minValue, max: maxValue };
			}
		}
	});

	return limits;
}

/**
 * Builds a flat key-value object of clamped numeric range values
 * to be used in URLSearchParams or API query strings.
 *
 * Example input:
 * {
 *   flowRateAt50hzInCfm: [0, 50000]
 * }
 *
 * With limit of { min: 0, max: 2500 }, this returns:
 * {
 *   flowRateAt50hzInCfmFrom: 0,
 *   flowRateAt50hzInCfmTo: 2500
 * }
 */
export function buildClampedRangeParams(
	ranges: Record<string, [number, number]>,
	limits?: FilterRangeLimits,
): Record<string, number> {
	const result: Record<string, number> = {};

	for (const [key, range] of Object.entries(ranges)) {
		if (!range) continue;

		let [from, to] = range;

		// Clamp using limits if available
		if (limits?.[key]) {
			const { min, max } = limits[key];
			from = Math.max(from ?? min, min);
			to = Math.min(to ?? max, max);
		}

		// Only include if valid
		if (from !== undefined && to !== undefined && from < to) {
			result[`${key}${NumericRangeFilterParam.From}`] = from;
			result[`${key}${NumericRangeFilterParam.To}`] = to;
		}
	}

	return result;
}

export function buildDynamicEnumParams(selectedEnums: EnumsFilterInput = {}): EnumsFilterInput {
	const dynamicEnumParams: EnumsFilterInput = {};

	for (const [key, values] of Object.entries(selectedEnums)) {
		if (Array.isArray(values) && values.length > 0) {
			// Append 's' to the key when posting to the API
			const pluralKey = key.endsWith('s') ? key : `${key}s`;
			dynamicEnumParams[pluralKey] = [...values];

			dynamicEnumParams[key] = [...values];
		}
	}

	return dynamicEnumParams;
}

export function parseEnumParams(searchParams: URLSearchParams, enumFilterKeys: string[]): EnumsFilterInput {
	const enums: EnumsFilterInput = {};
	for (const key of enumFilterKeys) {
		const val = searchParams.get(key);
		if (val) {
			enums[key] = val.split(',');
		}
	}
	return enums;
}

export function extractEnumFilters(config: ProductFilter[]): string[] {
	return config.filter((f) => f.configuration.$type === 'enumFilter').map((f) => f.configuration.attribute);
}
